/**
 * Import Wizard State Machine
 *
 * XState machine that manages the Import Wizard lifecycle.
 *
 * Architecture: Unified OPERATING flow
 * All save/navigation operations go through a single OPERATING state:
 *   PREPARING → SAVING → [EXECUTING] → [POLLING] → RELOADING → ROUTING
 *
 * State Chart:
 *
 *                              ┌─────────────────┐
 *                              │  initializing   │
 *                              │  invoke:        │
 *                              │  fetchInitialData│
 *                              └────────┬────────┘
 *                                       │
 *                 ┌─────────────────────┼─────────────────────┐
 *                 │ (status=running)    │ (status=idle)       │
 *                 ▼                     │                     │
 *        ┌─────────────────┐            │                     │
 *        │waitingForProcess│            │                     │
 *        │  invoke:        │            │                     │
 *        │  pollForIdle    │            │                     │
 *        └────────┬────────┘            │                     │
 *                 │ POLL_COMPLETE       │                     │
 *                 │                     │                     │
 *                 └─────────────────────┴─────────────────────┘
 *                                       │
 *                                       ▼
 *                              ┌─────────────────┐
 *                              │  firstRouting   │ (transient)
 *                              └────────┬────────┘
 *                                       │
 *        ┌────────────────────────────┬─┴──────────────────────────┐
 *        │                            │                            │
 *        ▼                            ▼                            ▼
 *     step1                        step2                        step3
 *        │                            │                            │
 *        │                            │                            │
 *        └────────────────────────────┴────────────────────────────┘
 *                                       │
 *                                       │ START_OPERATION
 *                                       ▼
 *                              ┌─────────────────────────────────────┐
 *                              │            OPERATING                │
 *                              │  ┌────────────┐                     │
 *                              │  │ preparing  │ apply transforms    │
 *                              │  └─────┬──────┘                     │
 *                              │        ▼                            │
 *                              │  ┌────────────┐                     │
 *                              │  │  saving    │ saveDataActor       │
 *                              │  └─────┬──────┘                     │
 *                              │        │                            │
 *                              │   (has operation?)                  │
 *                              │    yes │     no                     │
 *                              │        ▼      │                     │
 *                              │  ┌────────────┐│                    │
 *                              │  │ executing  ││ executeOperation   │
 *                              │  └─────┬──────┘│                    │
 *                              │        ▼       │                    │
 *                              │  ┌────────────┐│                    │
 *                              │  │  polling   ││ pollForIdle        │
 *                              │  └─────┬──────┘│                    │
 *                              │        │       │                    │
 *                              │        └───┬───┘                    │
 *                              │            ▼                        │
 *                              │  ┌────────────┐                     │
 *                              │  │ reloading  │ refetchData         │
 *                              │  └─────┬──────┘                     │
 *                              │        ▼                            │
 *                              │  ┌────────────┐                     │
 *                              │  │  routing   │ computeTargetStep   │
 *                              │  └─────┬──────┘                     │
 *                              └────────┼────────────────────────────┘
 *                                       │
 *                                       ▼
 *                               target step (1/2/3/4)
 */

import { setup, assign } from 'xstate'
import {
  fetchInitialDataActor,
  pollForIdleActor,
  refetchDataActor,
  validateDealsActor,
  saveDataActor,
  executeOperationActor,
  type FetchInitialDataOutput,
  type RefetchDataOutput,
} from './services'
import {
  WizardContext,
  WizardEvent,
  WizardStep,
  InternalState,
  NestedState,
  EventType,
  WizardMachineInput,
  MACHINE_ID,
  computeStepAvailabilityCounts,
  computeAvailableSteps,
  OperatingPhase,
  OperationType,
  Operation,
  operationNeedsExecution,
  computeTargetStep,
} from './types'
import {
  prepareDataForPersist,
  setReadyByHash,
  setMatchedChainByHash,
  setMatchedDoorByHash,
} from '../importDataTransforms'
import { InternalColumns, ImportData } from '../importer-types'

/**
 * Import Wizard State Machine
 */
export const wizardMachine = setup({
  types: {
    context: {} as WizardContext,
    events: {} as WizardEvent,
    input: {} as WizardMachineInput,
  },
  actors: {
    fetchInitialData: fetchInitialDataActor,
    pollForIdle: pollForIdleActor,
    refetchData: refetchDataActor,
    saveData: saveDataActor,
    executeOperation: executeOperationActor,
    validateDeals: validateDealsActor,
  },
  guards: {
    isProcessRunning: (_, params: { status: string }) =>
      params.status === 'running',
    hasStep1: ({ context }) =>
      context.availableSteps.includes(WizardStep.STEP1_CONFIRM),
    hasStep2: ({ context }) =>
      context.availableSteps.includes(WizardStep.STEP2_REVIEW),
    hasStep3: ({ context }) =>
      context.availableSteps.includes(WizardStep.STEP3_DECIDE),
    // NEXT event guard
    hasDeselections: ({ event }) => {
      const e = event as { deselectedHashes?: string[] }
      return (e.deselectedHashes?.length ?? 0) > 0
    },
    // Step 4: auto-trigger validation gate on re-entry after fix
    dealValidationPending: ({ context }) => context.dealValidationPending,
    // Guards for OPERATING.ROUTING
    operationNeedsExecution: ({ context }) =>
      operationNeedsExecution(context.operation),
    targetIsStep1: ({ context }) =>
      context.targetStep === WizardStep.STEP1_CONFIRM,
    targetIsStep2: ({ context }) =>
      context.targetStep === WizardStep.STEP2_REVIEW,
    targetIsStep3: ({ context }) =>
      context.targetStep === WizardStep.STEP3_DECIDE,
    targetIsStep4: ({ context }) =>
      context.targetStep === WizardStep.STEP4_IMPORT,
  },
  actions: {
    /**
     * Update import data and recompute available steps.
     * Used both for initial load (with importName) and refetch (without).
     */
    setDataAndComputeSteps: assign(({ context, event }) => {
      // Union type for both initial fetch and refetch outputs
      type DataEventOutput = FetchInitialDataOutput | RefetchDataOutput
      const eventData = (event as { output?: DataEventOutput }).output
      if (!eventData?.data) {
        return {}
      }

      const data = eventData.data
      const skipMatching = eventData.skipMatching ?? context.skipMatching

      const counts = computeStepAvailabilityCounts(data)
      const availableSteps = computeAvailableSteps(counts, skipMatching)

      return {
        importData: data,
        skipMatching,
        availableSteps,
        // Only set importName if present (initial load has it, refetch doesn't)
        ...('importName' in eventData && { importName: eventData.importName }),
      }
    }),

    incrementDataVersion: assign({
      dataVersion: ({ context }) => context.dataVersion + 1,
    }),

    setError: assign({
      error: ({ event }) => {
        if (event.type === 'POLL_ERROR') {
          return event.error
        }
        const err = (event as { error?: Error | string }).error
        if (err instanceof Error) return err.message
        if (typeof err === 'string') return err
        return 'An unexpected error occurred'
      },
    }),

    clearError: assign({
      error: null,
    }),

    // =========================================================================
    // OPERATING flow actions
    // =========================================================================

    /**
     * Create an operation from START_OPERATION event.
     * Stores the operation and computes target step.
     */
    createOperation: assign(({ context, event }) => {
      if (event.type !== EventType.START_OPERATION) return {}

      // Use provided data or fall back to current importData
      const dataToUse = event.data ?? context.importData

      const operation: Operation = {
        type: event.operation,
        direction: event.direction ?? 'stay',
        originStep: event.originStep,
        preparedData: dataToUse,
      }

      // Compute target step based on current availableSteps
      // This will be recomputed after reload with fresh availableSteps
      const targetStep = computeTargetStep(operation, context.availableSteps)

      return { operation, targetStep }
    }),

    /**
     * Prepare data for persistence by applying transforms.
     * Called in OPERATING.PREPARING phase.
     */
    prepareOperationData: assign(({ context }) => {
      if (!context.operation?.preparedData) return {}

      const preparedData = prepareDataForPersist(context.operation.preparedData)

      return {
        operation: {
          ...context.operation,
          preparedData,
        },
      }
    }),

    /**
     * Recompute target step after data reload.
     * Uses fresh availableSteps computed from reloaded data.
     */
    recomputeTargetStep: assign(({ context }) => {
      if (!context.operation) return {}

      const targetStep = computeTargetStep(
        context.operation,
        context.availableSteps
      )

      return { targetStep }
    }),

    /**
     * Clear the operation after OPERATING completes.
     */
    clearOperation: assign({
      operation: null,
      targetStep: null,
    }),

    // =========================================================================
    // NEXT event actions
    // =========================================================================

    /**
     * Store NEXT event payload for the deselect modal flow.
     * Updates importData with the component's latest local data and
     * stores deselected hashes for use on CONFIRM_DESELECT.
     */
    storeNextData: assign(({ event }) => {
      const e = event as {
        data?: ImportData
        deselectedHashes?: string[]
      }
      return {
        ...(e.data && { importData: e.data }),
        deselectedHashes: e.deselectedHashes ?? [],
      }
    }),

    /**
     * Create a forward navigation or set-companies operation from the NEXT event.
     * Parameterized with originStep and optional operationType.
     */
    createNextOperation: assign(
      (
        { context, event },
        params: { originStep: WizardStep; operationType?: OperationType }
      ) => {
        const e = event as { data?: ImportData }
        const dataToUse = e.data ?? context.importData

        const operation: Operation = {
          type: params.operationType ?? OperationType.NAVIGATE,
          direction: 'forward',
          originStep: params.originStep,
          preparedData: dataToUse,
        }

        const targetStep = computeTargetStep(operation, context.availableSteps)

        return { operation, targetStep }
      }
    ),

    /**
     * Apply deselection transforms and create a forward navigation operation.
     * Called on CONFIRM_DESELECT — applies transforms then goes through OPERATING.
     */
    createDeselectionOperation: assign(({ context }) => {
      let data = context.importData
      if (!data || context.deselectedHashes.length === 0) {
        return {}
      }

      // Apply deselection transforms: clear matches, set READY=false
      data = setReadyByHash(data, context.deselectedHashes, false)
      for (const hash of context.deselectedHashes) {
        data = setMatchedChainByHash(data, hash, null, false)
        data = setMatchedDoorByHash(data, hash, null, false)
      }

      const operation: Operation = {
        type: OperationType.NAVIGATE,
        direction: 'forward',
        originStep: WizardStep.STEP1_CONFIRM,
        preparedData: data,
      }

      const targetStep = computeTargetStep(operation, context.availableSteps)

      return { operation, targetStep, deselectedHashes: [] }
    }),

    /**
     * Clear stored deselection data on cancel.
     */
    clearDeselectionData: assign({
      deselectedHashes: [] as string[],
    }),

    // =========================================================================
    // Deal validation actions
    // =========================================================================

    setDealValidationData: assign({
      dealValidationData: ({ event }) => {
        const output = (
          event as { output?: WizardContext['dealValidationData'] }
        ).output
        return output ?? null
      },
    }),

    clearDealValidationData: assign({
      dealValidationData: null,
    }),

    setDealValidationPending: assign({
      dealValidationPending: true,
    }),

    clearDealValidationPending: assign({
      dealValidationPending: false,
    }),

    /**
     * Create a SET_DEALS operation after deal validation passes.
     */
    createDealsOperation: assign(({ context }) => {
      const operation: Operation = {
        type: OperationType.SET_DEALS,
        direction: 'stay',
        originStep: WizardStep.STEP4_IMPORT,
        preparedData: context.importData,
      }

      return {
        operation,
        targetStep: WizardStep.STEP4_IMPORT,
      }
    }),

    /**
     * Create a NAVIGATE operation that saves the deal product fix.
     * Applies the product to a copy of the data (does NOT mutate context.importData).
     * After OPERATING saves + reloads, the validation gate re-triggers automatically.
     */
    createDealProductFixOperation: assign(({ context, event }) => {
      if (event.type !== EventType.DEAL_PRODUCT_SELECTED) return {}
      if (!context.importData) return {}

      const { productName } = event

      const updatedRows = context.importData.rows.map((row) => ({
        ...row,
        [InternalColumns.DEAL_PRODUCT]: productName,
      }))

      const headerExists = context.importData.headers.some(
        (h) =>
          h.original === InternalColumns.DEAL_PRODUCT ||
          h.mapped === 'deal_product'
      )

      const updatedHeaders = headerExists
        ? context.importData.headers
        : [
            ...context.importData.headers,
            {
              original: InternalColumns.DEAL_PRODUCT,
              mapped: 'deal_product',
              label: 'Deal Product',
            },
          ]

      const preparedData: ImportData = {
        ...context.importData,
        rows: updatedRows,
        headers: updatedHeaders,
      }

      const operation: Operation = {
        type: OperationType.NAVIGATE,
        direction: 'stay',
        originStep: WizardStep.STEP4_IMPORT,
        preparedData,
      }

      return {
        operation,
        targetStep: WizardStep.STEP4_IMPORT,
      }
    }),

    /**
     * Create a NAVIGATE operation that saves validation fixes (account owner / sales stage mapping).
     * Applies fixes to a copy of the data (does NOT mutate context.importData).
     * After OPERATING saves + reloads, the validation gate re-triggers automatically.
     */
    createValidationFixOperation: assign(({ context, event }) => {
      if (event.type !== EventType.DEALS_VALIDATION_FIXED) return {}
      if (!context.importData) return {}

      const { changes, columnName } = event

      const header = context.importData.headers.find(
        (h) => h.mapped === columnName
      )
      if (!header) {
        throw new Error(
          `Header not found for column: ${columnName}. ` +
            `Cannot apply validation fixes.`
        )
      }
      const originalKey = header.original

      const changeMap = new Map<string, string>()
      for (const change of changes) {
        if (change.mapped) {
          changeMap.set(change.original, change.mapped)
        }
      }

      const updatedRows = context.importData.rows.map((row) => {
        const currentValue = row[originalKey]
        if (typeof currentValue === 'string' && changeMap.has(currentValue)) {
          return { ...row, [originalKey]: changeMap.get(currentValue) }
        }
        return row
      })

      const preparedData: ImportData = {
        ...context.importData,
        rows: updatedRows,
      }

      const operation: Operation = {
        type: OperationType.NAVIGATE,
        direction: 'stay',
        originStep: WizardStep.STEP4_IMPORT,
        preparedData,
      }

      return {
        operation,
        targetStep: WizardStep.STEP4_IMPORT,
      }
    }),
  },
}).createMachine({
  id: MACHINE_ID,
  initial: InternalState.INITIALIZING,
  context: ({ input }) => ({
    importNumber: input.importNumber,
    importName: '',
    importData: null,
    skipMatching: false,
    availableSteps: [],
    dataVersion: 0,
    operation: null,
    targetStep: null,
    error: null,
    dealValidationData: null,
    dealValidationPending: false,
    deselectedHashes: [],
  }),

  states: {
    // ==================== INITIALIZING ====================
    [InternalState.INITIALIZING]: {
      invoke: {
        src: 'fetchInitialData',
        input: ({ context }) => ({ importNumber: context.importNumber }),
        onDone: [
          {
            // Process is running - go wait for it
            guard: {
              type: 'isProcessRunning',
              params: ({ event }) => ({ status: event.output.status }),
            },
            target: InternalState.WAITING_FOR_PROCESS,
            actions: ['setDataAndComputeSteps'],
          },
          {
            // No process running - route to first available step
            target: 'firstRouting',
            actions: ['setDataAndComputeSteps', 'incrementDataVersion'],
          },
        ],
        onError: {
          target: InternalState.ERROR,
          actions: ['setError'],
        },
      },
    },

    // ==================== WAITING FOR PROCESS ====================
    // Poll until backend process completes, then refetch and route
    [InternalState.WAITING_FOR_PROCESS]: {
      invoke: {
        src: 'pollForIdle',
        input: ({ context }) => ({ importNumber: context.importNumber }),
      },
      on: {
        POLL_COMPLETE: {
          target: 'waitingRefresh',
        },
        POLL_ERROR: {
          target: InternalState.ERROR,
          actions: ['setError'],
        },
      },
    },

    // Refetch data after waiting for process, then route to first step
    waitingRefresh: {
      invoke: {
        src: 'refetchData',
        input: ({ context }) => ({ importNumber: context.importNumber }),
        onDone: {
          target: 'firstRouting',
          actions: ['setDataAndComputeSteps', 'incrementDataVersion'],
        },
        onError: {
          target: InternalState.ERROR,
          actions: ['setError'],
        },
      },
    },

    // ==================== FIRST ROUTING ====================
    // Transient state to determine initial step based on available steps
    firstRouting: {
      always: [
        { target: WizardStep.STEP1_CONFIRM, guard: 'hasStep1' },
        { target: WizardStep.STEP2_REVIEW, guard: 'hasStep2' },
        { target: WizardStep.STEP3_DECIDE, guard: 'hasStep3' },
        { target: WizardStep.STEP4_IMPORT },
      ],
    },

    // ==================== STEP 1: CONFIRM MATCHES ====================
    [WizardStep.STEP1_CONFIRM]: {
      initial: NestedState.VIEWING,
      on: {
        // Back/save still use START_OPERATION
        [EventType.START_OPERATION]: {
          target: InternalState.OPERATING,
          actions: 'createOperation',
        },
      },
      states: {
        [NestedState.VIEWING]: {
          on: {
            [EventType.NEXT]: [
              {
                // Has deselections → show confirmation modal
                guard: 'hasDeselections',
                target: NestedState.DESELECT_MODAL,
                actions: 'storeNextData',
              },
              {
                // No deselections → navigate forward
                target: `#${MACHINE_ID}.${InternalState.OPERATING}`,
                actions: {
                  type: 'createNextOperation',
                  params: { originStep: WizardStep.STEP1_CONFIRM },
                },
              },
            ],
          },
        },
        [NestedState.DESELECT_MODAL]: {
          on: {
            [EventType.CONFIRM_DESELECT]: {
              // Apply deselection transforms → OPERATING (save → refetch → route)
              target: `#${MACHINE_ID}.${InternalState.OPERATING}`,
              actions: 'createDeselectionOperation',
            },
            [EventType.CANCEL_DESELECT]: {
              target: NestedState.VIEWING,
              actions: 'clearDeselectionData',
            },
          },
        },
      },
    },

    // ==================== STEP 2: REVIEW PENDING ====================
    [WizardStep.STEP2_REVIEW]: {
      on: {
        [EventType.NEXT]: {
          target: InternalState.OPERATING,
          actions: {
            type: 'createNextOperation',
            params: { originStep: WizardStep.STEP2_REVIEW },
          },
        },
        // Back/save still use START_OPERATION
        [EventType.START_OPERATION]: {
          target: InternalState.OPERATING,
          actions: 'createOperation',
        },
      },
    },

    // ==================== STEP 3: DECIDE UNMATCHED ====================
    [WizardStep.STEP3_DECIDE]: {
      initial: NestedState.VIEWING,
      on: {
        // Back/save still use START_OPERATION
        [EventType.START_OPERATION]: {
          target: InternalState.OPERATING,
          actions: 'createOperation',
        },
      },
      states: {
        [NestedState.VIEWING]: {
          on: {
            [EventType.NEXT]: {
              target: NestedState.CONFIRM_MODAL,
              actions: 'storeNextData',
            },
          },
        },
        [NestedState.CONFIRM_MODAL]: {
          on: {
            [EventType.CONFIRM_MATCHES]: {
              target: `#${MACHINE_ID}.${InternalState.OPERATING}`,
              actions: {
                type: 'createNextOperation',
                params: {
                  originStep: WizardStep.STEP3_DECIDE,
                  operationType: OperationType.SET_COMPANIES,
                },
              },
            },
            [EventType.CANCEL_CONFIRM]: {
              target: NestedState.VIEWING,
            },
          },
        },
      },
    },

    // ==================== STEP 4: IMPORT ====================
    // Deal validation is a gate: fixes go through OPERATING (save → reload → route
    // back here) and the `always` guard on VIEWING re-triggers validation automatically
    // while dealValidationPending is true.
    [WizardStep.STEP4_IMPORT]: {
      initial: NestedState.VIEWING,
      on: {
        [EventType.START_OPERATION]: {
          target: InternalState.OPERATING,
          actions: 'createOperation',
        },
      },
      states: {
        [NestedState.VIEWING]: {
          always: [
            {
              guard: 'dealValidationPending',
              target: NestedState.VALIDATING_DEALS,
            },
          ],
          on: {
            [EventType.VALIDATE_DEALS]: {
              target: NestedState.VALIDATING_DEALS,
              actions: 'setDealValidationPending',
            },
          },
        },

        // Validation gate — runs checks, routes to modal or OPERATING
        [NestedState.VALIDATING_DEALS]: {
          invoke: {
            src: 'validateDeals',
            input: ({ context }) => ({
              importNumber: context.importNumber,
              importData: context.importData,
            }),
            onDone: [
              {
                guard: ({ event }) => event.output?.type === 'product_required',
                target: NestedState.DEAL_PRODUCT_MODAL,
                actions: ['setDealValidationData'],
              },
              {
                guard: ({ event }) => event.output?.type === 'mapping',
                target: NestedState.DEAL_VALIDATION_MODAL,
                actions: ['setDealValidationData'],
              },
              {
                // Validation passed — clear pending flag and import deals
                target: `#${MACHINE_ID}.${InternalState.OPERATING}`,
                actions: [
                  'clearDealValidationData',
                  'clearDealValidationPending',
                  'createDealsOperation',
                ],
              },
            ],
            onError: {
              target: `#${MACHINE_ID}.${InternalState.ERROR}`,
              actions: ['setError'],
            },
          },
        },

        [NestedState.DEAL_PRODUCT_MODAL]: {
          on: {
            [EventType.DEAL_PRODUCT_SELECTED]: {
              // Fix goes through OPERATING (save → reload → route back → auto-validate)
              target: `#${MACHINE_ID}.${InternalState.OPERATING}`,
              actions: [
                'createDealProductFixOperation',
                'clearDealValidationData',
              ],
            },
            [EventType.CANCEL_DEAL_VALIDATION]: {
              target: NestedState.VIEWING,
              actions: [
                'clearDealValidationData',
                'clearDealValidationPending',
              ],
            },
          },
        },

        [NestedState.DEAL_VALIDATION_MODAL]: {
          on: {
            [EventType.DEALS_VALIDATION_FIXED]: {
              // Fix goes through OPERATING (save → reload → route back → auto-validate)
              target: `#${MACHINE_ID}.${InternalState.OPERATING}`,
              actions: [
                'createValidationFixOperation',
                'clearDealValidationData',
              ],
            },
            [EventType.CANCEL_DEAL_VALIDATION]: {
              target: NestedState.VIEWING,
              actions: [
                'clearDealValidationData',
                'clearDealValidationPending',
              ],
            },
          },
        },
      },
    },

    // ==================== OPERATING ====================
    // Unified async flow: prepare → save → [execute] → [poll] → reload → route
    [InternalState.OPERATING]: {
      initial: OperatingPhase.PREPARING,
      states: {
        [OperatingPhase.PREPARING]: {
          always: {
            target: OperatingPhase.SAVING,
            actions: 'prepareOperationData',
          },
        },

        [OperatingPhase.SAVING]: {
          invoke: {
            src: 'saveData',
            input: ({ context }) => ({
              importNumber: context.importNumber,
              data: context.operation!.preparedData!,
            }),
            onDone: [
              {
                guard: 'operationNeedsExecution',
                target: OperatingPhase.EXECUTING,
              },
              {
                target: OperatingPhase.RELOADING,
              },
            ],
            onError: {
              target: `#${MACHINE_ID}.${InternalState.ERROR}`,
              actions: 'setError',
            },
          },
        },

        [OperatingPhase.EXECUTING]: {
          invoke: {
            src: 'executeOperation',
            input: ({ context }) => ({
              importNumber: context.importNumber,
              operationType: context.operation!.type,
            }),
            onDone: OperatingPhase.POLLING,
            onError: {
              target: `#${MACHINE_ID}.${InternalState.ERROR}`,
              actions: 'setError',
            },
          },
        },

        [OperatingPhase.POLLING]: {
          invoke: {
            src: 'pollForIdle',
            input: ({ context }) => ({ importNumber: context.importNumber }),
          },
          on: {
            POLL_COMPLETE: OperatingPhase.RELOADING,
            POLL_ERROR: {
              target: `#${MACHINE_ID}.${InternalState.ERROR}`,
              actions: 'setError',
            },
          },
        },

        [OperatingPhase.RELOADING]: {
          invoke: {
            src: 'refetchData',
            input: ({ context }) => ({ importNumber: context.importNumber }),
            onDone: {
              target: OperatingPhase.ROUTING,
              actions: [
                'setDataAndComputeSteps',
                'incrementDataVersion',
                'recomputeTargetStep',
              ],
            },
            onError: {
              target: `#${MACHINE_ID}.${InternalState.ERROR}`,
              actions: 'setError',
            },
          },
        },

        [OperatingPhase.ROUTING]: {
          always: [
            {
              guard: 'targetIsStep1',
              target: `#${MACHINE_ID}.${WizardStep.STEP1_CONFIRM}`,
              actions: 'clearOperation',
            },
            {
              guard: 'targetIsStep2',
              target: `#${MACHINE_ID}.${WizardStep.STEP2_REVIEW}`,
              actions: 'clearOperation',
            },
            {
              guard: 'targetIsStep3',
              target: `#${MACHINE_ID}.${WizardStep.STEP3_DECIDE}`,
              actions: 'clearOperation',
            },
            {
              target: `#${MACHINE_ID}.${WizardStep.STEP4_IMPORT}`,
              actions: 'clearOperation',
            },
          ],
        },
      },
    },

    // ==================== ERROR ====================
    [InternalState.ERROR]: {
      on: {
        [EventType.RETRY]: {
          target: InternalState.INITIALIZING,
          actions: ['clearError'],
        },
      },
    },
  },
})

export type WizardMachine = typeof wizardMachine
